'use client';

import type { RowData } from '@tanstack/react-table';
import type { Column } from '@/lib/table-features';
import { useLocale, useTranslations } from 'next-intl';
import { EllipsisVertical, EyeOff, Pin, PinOff } from 'lucide-react';

import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  CaretSortIcon,
  Cross2Icon
} from '@radix-ui/react-icons';

interface DataTableColumnHeaderProps<
  TData extends RowData,
  TValue
> extends React.ComponentProps<typeof DropdownMenuTrigger> {
  column: Column<TData, TValue>;
  title: string;
}

export function DataTableColumnHeader<TData extends RowData, TValue>({
  column,
  title,
  className,
  ...props
}: DataTableColumnHeaderProps<TData, TValue>) {
  const tc = useTranslations('common');
  const isRtl = useLocale() === 'ar';

  // In RTL, swap TanStack pin direction so "Pin Left" = physical left.
  // CSS swap in getCommonPinningStyles completes the double-swap.
  const pinLeftValue: 'start' | 'end' = isRtl ? 'end' : 'start';
  const pinRightValue: 'start' | 'end' = isRtl ? 'start' : 'end';

  const canPin =
    column.getCanPin() && column.id !== 'select' && column.id !== 'actions';

  if (!column.getCanSort() && !column.getCanHide() && !canPin) {
    return <div className={cn(className)}>{title}</div>;
  }

  const isPinned = column.getIsPinned();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(
          'hover:bg-accent focus:ring-ring data-[state=open]:bg-accent [&_svg]:text-muted-foreground -ms-1.5 flex h-8 items-center gap-1.5 rounded-md px-2 py-1.5 select-none focus:ring-1 focus:outline-none [&_svg]:size-4 [&_svg]:shrink-0',
          className
        )}
        {...props}
      >
        {title}
        {column.getCanSort() && column.getIsSorted() === 'desc' ? (
          <ChevronDownIcon />
        ) : column.getIsSorted() === 'asc' ? (
          <ChevronUpIcon />
        ) : (
          <CaretSortIcon />
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align='start' className='w-28'>
        {column.getCanSort() && (
          <>
            <DropdownMenuCheckboxItem
              className='[&_svg]:text-muted-foreground relative ps-2 pe-8 [&>span:first-child]:right-2 [&>span:first-child]:left-auto'
              checked={column.getIsSorted() === 'asc'}
              onClick={() => column.toggleSorting(false)}
            >
              <ChevronUpIcon />
              {tc('sortAsc')}
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              className='[&_svg]:text-muted-foreground relative ps-2 pe-8 [&>span:first-child]:right-2 [&>span:first-child]:left-auto'
              checked={column.getIsSorted() === 'desc'}
              onClick={() => column.toggleSorting(true)}
            >
              <ChevronDownIcon />
              {tc('sortDesc')}
            </DropdownMenuCheckboxItem>
            {column.getIsSorted() && (
              <DropdownMenuItem
                className='[&_svg]:text-muted-foreground ps-2'
                onClick={() => column.clearSorting()}
              >
                <Cross2Icon />
                {tc('sortReset')}
              </DropdownMenuItem>
            )}
          </>
        )}
        {canPin && (
          <>
            {(column.getCanSort() || column.getCanHide()) && (
              <DropdownMenuSeparator />
            )}
            {isPinned !== pinLeftValue && (
              <DropdownMenuItem
                className='[&_svg]:text-muted-foreground ps-2'
                onClick={() => column.pin(pinLeftValue)}
              >
                <Pin className='-rotate-45' />
                {tc('pinLeft')}
              </DropdownMenuItem>
            )}
            {isPinned !== pinRightValue && (
              <DropdownMenuItem
                className='[&_svg]:text-muted-foreground ps-2'
                onClick={() => column.pin(pinRightValue)}
              >
                <Pin className='rotate-45' />
                {tc('pinRight')}
              </DropdownMenuItem>
            )}
            {isPinned && (
              <DropdownMenuItem
                className='[&_svg]:text-muted-foreground ps-2'
                onClick={() => column.pin(false)}
              >
                <PinOff />
                {tc('unpin')}
              </DropdownMenuItem>
            )}
          </>
        )}
        {column.getCanHide() && (
          <>
            {(column.getCanSort() || canPin) && <DropdownMenuSeparator />}
            <DropdownMenuCheckboxItem
              className='[&_svg]:text-muted-foreground relative ps-2 pe-8 [&>span:first-child]:right-2 [&>span:first-child]:left-auto'
              checked={!column.getIsVisible()}
              onClick={() => column.toggleVisibility(false)}
            >
              <EyeOff />
              {tc('hideColumn')}
            </DropdownMenuCheckboxItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
